/**
 * PreviousVisitValidationModule
 * Shared logic for comparing grading form answers to previous or selected visit (from Adjudication).
 * Use in both grading and additional grading controllers to avoid duplication.
 *
 * Options: { gradingForm, gradingFormSchema, getLabelFromSchema(schema, key, value), previousVisitGradingUrl, initialOverrides }
 * - previousVisitGradingUrl: full URL that returns { message: "Found", data: {...}, previous_visit_label, previous_date, reference_user_name, reference_user_role, transfer_status }
 *   Grading forms use the grading workflow (prefer completed adjudication, else grader answers).
 *   Adjudication UIs pass reference_source=adjudication (completed adjudication only; no grading fallback).
 * - initialOverrides: from saved responses.previous_visit_overrides
 *
 * Controller usage: create module after form is ready, call fetchPreviousVisitGradingData(),
 * attachPreviousVisitConsistencyOnBlur() on form render (delegated focusout),
 * afterProgrammaticValueSet(component, value) after setValue append/autofill,
 * notifyAppendedFieldValue(input, value) after SDAR/direct DOM append,
 * handleFormChange(changed) on form change (non-numeric fields),
 * and submission.previous_visit_overrides = module.getOverrides() when submitting.
 *
 * Report UI: openLongitudinalChangesReportModal / confirmLongitudinalReportBeforeSubmit (same HTML as the report button).
 *
 * Adjudication display: use PreviousVisitValidationModule.formatOverrideNote(override) for label + title.
 */
import FormioUtils from "formiojs/utils";

export default class PreviousVisitValidationModule {
  constructor(options) {
    this.gradingForm = options.gradingForm;
    this.gradingFormSchema = options.gradingFormSchema;
    this.getLabelFromSchema = options.getLabelFromSchema;
    this.previousVisitGradingUrl = options.previousVisitGradingUrl || null;
    this.overrides = options.initialOverrides || {};
    this.dataByMode = { previous: null, byVisit: {} };
    this._validationSuppressDepth = 0;
    this._focusoutValueHint = {};
    this._focusoutFormEl = null;
    this._focusoutHandler = null;
    this._longitudinalAlertVisible = false;
    this._lastAlertSnapshot = {};
    this._longitudinalAlertQueue = [];
    this._longitudinalAlertProcessing = false;
  }

  _isLongitudinalAlertOpen() {
    return (
      this._longitudinalAlertVisible ||
      (typeof window.Swal !== "undefined" &&
        typeof window.Swal.isVisible === "function" &&
        window.Swal.isVisible())
    );
  }

  _shouldSkipDuplicateAlert(key, previousValue, currentValue) {
    const snap = this._lastAlertSnapshot && this._lastAlertSnapshot[key];
    if (!snap) return false;
    return (
      this.valuesEqual(snap.previousValue, previousValue) &&
      this.valuesEqual(snap.currentValue, currentValue)
    );
  }

  _enqueueLongitudinalAlert(presentAlert) {
    this._longitudinalAlertQueue.push(presentAlert);
    this._processLongitudinalAlertQueue();
  }

  _processLongitudinalAlertQueue() {
    if (this._longitudinalAlertProcessing) return;
    if (!this._longitudinalAlertQueue.length) return;
    if (this._isLongitudinalAlertOpen()) {
      window.setTimeout(() => this._processLongitudinalAlertQueue(), 200);
      return;
    }

    const presentAlert = this._longitudinalAlertQueue.shift();
    if (typeof presentAlert !== "function") {
      this._processLongitudinalAlertQueue();
      return;
    }

    this._longitudinalAlertProcessing = true;
    Promise.resolve()
      .then(() => presentAlert())
      .finally(() => {
        this._longitudinalAlertProcessing = false;
        window.setTimeout(() => this._processLongitudinalAlertQueue(), 100);
      });
  }

  _finishLongitudinalAlert() {
    this._longitudinalAlertVisible = false;
    window.setTimeout(() => {
      if (!this._isLongitudinalAlertOpen()) {
        this._restorePageInteractivity();
      }
    }, 50);
  }

  _extractKeyFromInput(input) {
    if (!input) return null;
    const name = input.getAttribute("name");
    if (!name) return null;
    const match = name.match(/^data\[([^\]]+)\]$/);
    return match ? match[1] : null;
  }

  _findComponentForInput(input) {
    if (!input || !this.gradingForm) return null;
    let found = null;
    this.gradingForm.everyComponent((component) => {
      if (found || !component?.element) return;
      if (component.element.contains(input)) found = component;
    });
    return found;
  }

  _isLongitudinalFocusoutInput(input) {
    if (!input) return false;
    if (input.matches("textarea")) return true;
    if (!input.matches("input")) return false;
    const inputType = (input.getAttribute("type") || "text").toLowerCase();
    return (
      inputType !== "radio" &&
      inputType !== "checkbox" &&
      inputType !== "hidden" &&
      inputType !== "file" &&
      inputType !== "button" &&
      inputType !== "submit"
    );
  }

  _runFocusoutValidation(component, valueHint) {
    if (!component || this._validationSuppressDepth > 0) return;
    window.setTimeout(() => {
      if (this._validationSuppressDepth > 0) return;
      this.checkPreviousVisitConsistency({
        changed: { component },
        ...(valueHint !== undefined ? { flags: { valueHint } } : {}),
      });
    }, 50);
  }

  _handleDelegatedFocusout(event) {
    if (this._validationSuppressDepth > 0 || !this.gradingForm) return;
    const target = event.target;
    if (!this._isLongitudinalFocusoutInput(target)) return;

    const keyFromName = this._extractKeyFromInput(target);
    let component = keyFromName
      ? this.gradingForm.getComponent(keyFromName)
      : null;
    if (!component) {
      component = this._findComponentForInput(target);
    }
    if (!component) return;

    const key =
      component.component?.key || component.key || keyFromName;
    if (!key) return;

    const type = (
      component.component?.type ||
      component.type ||
      ""
    ).toLowerCase();
    if (type !== "number" && type !== "textfield" && type !== "textarea") {
      return;
    }

    const gv = this.getGradingValidationForKey(key);
    if (!gv || gv.status !== "Required") return;
    if (!this.usesNumericLongitudinalRules(type, gv)) return;

    let valueHint;
    if (Object.prototype.hasOwnProperty.call(this._focusoutValueHint, key)) {
      valueHint = this._focusoutValueHint[key];
      delete this._focusoutValueHint[key];
    }

    this._runFocusoutValidation(component, valueHint);
  }

  _runWithoutValidation(callback) {
    this._validationSuppressDepth += 1;
    try {
      callback();
    } finally {
      window.setTimeout(() => {
        this._validationSuppressDepth = Math.max(
          0,
          this._validationSuppressDepth - 1,
        );
      }, 300);
    }
  }

  _restorePageInteractivity() {
    document.body.classList.remove(
      "swal2-shown",
      "swal2-height-auto",
      "swal2-iosfix",
      "swal2-no-backdrop",
      "swal2-toast-shown",
    );
    document.documentElement.classList.remove(
      "swal2-shown",
      "swal2-height-auto",
      "swal2-iosfix",
    );
    document.body.style.overflow = "";
    document.body.style.paddingRight = "";
    document.documentElement.style.overflow = "";
    document.body.style.pointerEvents = "";

    Array.from(document.body.children).forEach((el) => {
      if (el.classList.contains("swal2-container")) return;

      if (el.hasAttribute("data-previous-aria-hidden")) {
        const previous = el.getAttribute("data-previous-aria-hidden");
        if (previous) {
          el.setAttribute("aria-hidden", previous);
        } else {
          el.removeAttribute("aria-hidden");
        }
        el.removeAttribute("data-previous-aria-hidden");
      } else {
        el.removeAttribute("aria-hidden");
      }
    });

    // Transparent containers still block clicks even when scroll works.
    document.querySelectorAll(".swal2-container").forEach((el) => el.remove());
  }

  _showReferenceVisitNotAvailableAlert(visitLabel) {
    const label = visitLabel || "selected visit";
    const msgHtml =
      '<p class="text-left mb-2"><strong>Reference Visit Not Available</strong></p>' +
      '<p class="text-left mb-2">The configured reference visit (' +
      label +
      ") does not contain a recorded value for this question.</p>" +
      '<p class="text-left mb-2">Longitudinal comparison cannot be performed.</p>' +
      '<p class="text-left mb-0">This condition will be treated as No Reference Available and no rule evaluation will occur.</p>';
    if (typeof window.Swal !== "undefined") {
      window.Swal.fire({
        icon: "info",
        title: "Reference Visit Not Available",
        html: msgHtml,
      });
    } else {
      window.alert(
        "Reference Visit Not Available\n\n" +
          "The configured reference visit (" +
          label +
          ") does not contain a recorded value for this question.\n" +
          "Longitudinal comparison cannot be performed.\n\n" +
          "This condition will be treated as No Reference Available and no rule evaluation will occur.",
      );
    }
  }

  _handleResolvedEdit(key) {
    this._longitudinalAlertVisible = false;
    if (this._lastAlertSnapshot) {
      delete this._lastAlertSnapshot[key];
    }
    window.setTimeout(() => {
      this._runWithoutValidation(() => {
        this.clearValue(key);
      });
      this._restorePageInteractivity();
      this._focusField(key);
      window.setTimeout(() => {
        this._restorePageInteractivity();
        this._focusField(key);
      }, 150);
    }, 50);
  }

  _collectCustomVisitIds() {
    const ids = new Set();
    const walk = (components) => {
      if (!Array.isArray(components)) return;
      for (let i = 0; i < components.length; i++) {
        const comp = components[i];
        if (!comp) continue;
        const gv = comp.grading_validation;
        if (gv && gv.compare_to === "custom" && gv.reference_visit_id) {
          ids.add(String(gv.reference_visit_id));
        }
        if (Array.isArray(comp.components) && comp.components.length) {
          walk(comp.components);
        }
      }
    };
    walk(this.gradingFormSchema && this.gradingFormSchema.components);
    return ids;
  }

  fetchPreviousVisitGradingData() {
    if (!this.previousVisitGradingUrl) return Promise.resolve();
    const sep = this.previousVisitGradingUrl.indexOf("?") >= 0 ? "&" : "?";
    const headers = {
      Accept: "application/json",
      "X-CSRF-Token":
        document.head
          .querySelector('meta[name="csrf-token"]')
          ?.getAttribute("content") || "",
    };
    const fetchOne = (compareTo, referenceVisitId) => {
      let url =
        this.previousVisitGradingUrl +
        sep +
        "compare_to=" +
        encodeURIComponent(compareTo);
      if (compareTo === "custom" && referenceVisitId)
        url += "&reference_visit_id=" + encodeURIComponent(referenceVisitId);
      return fetch(url, { method: "GET", headers })
        .then((res) => (res.ok ? res.json() : Promise.reject(res)))
        .then((data) => {
          if (data.completed === false) {
            return {
              completed: false,
              message:
                data.message ||
                "You have selected an uncompleted visit to compare.",
              previous_visit_label: data.previous_visit_label,
              previous_date: data.previous_date,
              reference_user_name: data.reference_user_name,
              reference_user_role: data.reference_user_role,
              transfer_status: data.transfer_status,
            };
          }
          if (data.message === "Found" && data.data) {
            return {
              data: data.data,
              previous_visit_label: data.previous_visit_label,
              previous_date: data.previous_date,
              reference_user_name: data.reference_user_name,
              reference_user_role: data.reference_user_role,
              transfer_status: data.transfer_status,
              history: Array.isArray(data.history) ? data.history : [],
            };
          }
          // Backend "no usable reference" messages (without completed:false) still
          // need to drive the Reference Visit Not Available alert in the UI.
          const msg = (data && data.message) || "";
          if (
            /uncompleted|no reference|no previous completed|no grading data|no adjudication|no stack found/i.test(
              msg,
            )
          ) {
            return {
              completed: false,
              message: msg,
              previous_visit_label: data.previous_visit_label,
              previous_date: data.previous_date,
              reference_user_name: data.reference_user_name,
              reference_user_role: data.reference_user_role,
              transfer_status: data.transfer_status,
            };
          }
          return null;
        })
        .catch(() => null);
    };

    const customIds = this._collectCustomVisitIds();
    const customFetches = Array.from(customIds).map((vid) =>
      fetchOne("custom", vid).then((result) => ({ visitId: vid, result })),
    );

    return Promise.all([fetchOne("previous"), ...customFetches]).then(
      (results) => {
        this.dataByMode.previous = results[0];
        this.dataByMode.byVisit = {};
        for (let i = 1; i < results.length; i++) {
          const { visitId, result } = results[i];
          this.dataByMode.byVisit[visitId] = result;
        }
      },
    );
  }

  getGradingValidationForKey(key) {
    const findIn = (components) => {
      if (!Array.isArray(components)) return null;
      for (let i = 0; i < components.length; i++) {
        const comp = components[i];
        if (!comp) continue;
        if (comp.key === key) return comp.grading_validation || null;
        if (Array.isArray(comp.components) && comp.components.length) {
          const nested = findIn(comp.components);
          if (nested) return nested;
        }
      }
      return null;
    };
    return findIn(this.gradingFormSchema && this.gradingFormSchema.components);
  }

  getDataValueByQuestionKey(data, key) {
    if (!data || typeof data !== "object" || !key) return undefined;
    if (Object.prototype.hasOwnProperty.call(data, key)) return data[key];

    const target = String(key).trim().toLowerCase();
    const keys = Object.keys(data);
    for (let i = 0; i < keys.length; i++) {
      const k = keys[i];
      if (String(k).trim().toLowerCase() === target) return data[k];
    }
    return undefined;
  }

  hasReferenceValue(value) {
    if (value === undefined || value === null) return false;
    if (typeof value === "string") return value.trim() !== "";
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "object") {
      const keys = Object.keys(value || {});
      if (!keys.length) return false;
      // For selectboxes-like objects, require at least one checked key.
      const anyTruthy = keys.some((k) => !!value[k]);
      return anyTruthy;
    }
    return true;
  }

  isLongitudinalSkipValue(value) {
    if (value === undefined || value === null) return false;
    const num = parseFloat(value);
    if (!isNaN(num) && num === 9999) return true;
    return String(value).trim() === "9999";
  }

  normalizeTransitionValue(value) {
    if (value === undefined || value === null) return "";
    if (typeof value === "string") return value.trim();
    return String(value).trim();
  }

  findTransitionRule(gv, previousValue, currentValue) {
    const rules = Array.isArray(gv?.transition_rules)
      ? gv.transition_rules
      : [];
    if (!rules.length) return null;
    const fromValue =
      this.normalizeTransitionValue(previousValue).toLowerCase();
    const toValue = this.normalizeTransitionValue(currentValue).toLowerCase();
    if (!fromValue && !toValue) return null;

    for (let i = 0; i < rules.length; i++) {
      const rule = rules[i] || {};
      const ruleFrom = this.normalizeTransitionValue(
        rule.from_value,
      ).toLowerCase();
      const ruleTo = this.normalizeTransitionValue(rule.to_value).toLowerCase();
      if (ruleFrom === fromValue && ruleTo === toValue) return rule;
    }
    return null;
  }

  hasExistingOverrideForCurrentChange(key, previousValue, currentValue) {
    const existing = this.overrides && this.overrides[key];
    if (!existing) return false;

    const prevNorm = this.normalizeTransitionValue(previousValue);
    const currNorm = this.normalizeTransitionValue(currentValue);

    // Transition-rule override
    if (existing.type === "transition_rule") {
      const fromNorm = this.normalizeTransitionValue(
        existing.from_value || existing.previous_value,
      );
      const toNorm = this.normalizeTransitionValue(
        existing.to_value || existing.current_value,
      );
      return fromNorm === prevNorm && toNorm === currNorm;
    }

    // Number-threshold or generic override
    const existingPrev = this.normalizeTransitionValue(existing.previous_value);
    const existingCurr = this.normalizeTransitionValue(existing.current_value);
    return existingPrev === prevNorm && existingCurr === currNorm;
  }

  usesNumericLongitudinalRules(type, gv) {
    if (!gv || gv.status !== "Required") return false;
    const baseOn = gv.base_on;
    const numericRule =
      baseOn === "Custom" ||
      baseOn === "Percentage" ||
      baseOn === "absolute_difference" ||
      baseOn === "not_less_than_previous";
    if (!numericRule) return false;
    const t = (type || "").toLowerCase();
    return t === "number" || t === "textfield" || t === "textarea";
  }

  attachPreviousVisitConsistencyOnBlur() {
    if (!this.gradingForm) return;
    const formEl = this.gradingForm.element;
    if (!formEl) return;

    if (this._focusoutFormEl === formEl && this._focusoutHandler) return;

    if (this._focusoutFormEl && this._focusoutHandler) {
      this._focusoutFormEl.removeEventListener(
        "focusout",
        this._focusoutHandler,
      );
    }

    this._focusoutHandler = (event) => this._handleDelegatedFocusout(event);
    this._focusoutFormEl = formEl;
    formEl.addEventListener("focusout", this._focusoutHandler);
  }

  afterProgrammaticValueSet(component, appendedValue) {
    if (!component) return;
    const key =
      component.key || (component.component && component.component.key);
    if (key && appendedValue !== undefined) {
      this._focusoutValueHint[key] = appendedValue;
      if (this._lastAlertSnapshot) {
        delete this._lastAlertSnapshot[key];
      }
    }

    window.setTimeout(() => {
      const input =
        (component.refs && component.refs.input && component.refs.input[0]) ||
        (component.element &&
          component.element.querySelector("input, textarea"));

      if (input) {
        input.dispatchEvent(new FocusEvent("focusout", { bubbles: true }));
        return;
      }

      const valueHint =
        key &&
        Object.prototype.hasOwnProperty.call(this._focusoutValueHint, key)
          ? this._focusoutValueHint[key]
          : appendedValue;
      if (key) delete this._focusoutValueHint[key];
      this._runFocusoutValidation(component, valueHint);
    }, 10);
  }

  notifyAppendedFieldValue(input, value) {
    if (!input) return;

    const keyFromName = this._extractKeyFromInput(input);
    let component =
      keyFromName && this.gradingForm
        ? this.gradingForm.getComponent(keyFromName)
        : null;
    if (!component) {
      component = this._findComponentForInput(input);
    }

    if (component) {
      if (typeof component.setValue === "function" && value !== undefined) {
        try {
          component.setValue(value, { noUpdateEvent: true });
        } catch (e) {
          try {
            component.setValue(value);
          } catch (e2) {}
        }
      }
      this.afterProgrammaticValueSet(component, value);
      return;
    }

    if (keyFromName && value !== undefined) {
      this._focusoutValueHint[keyFromName] = value;
    }
    window.setTimeout(() => {
      input.dispatchEvent(new FocusEvent("focusout", { bubbles: true }));
    }, 10);
  }

  handleFormChange(changed) {
    const component =
      changed?.changed?.component || changed?.flags?.changed?.component;
    if (!component) return;

    const compType = (
      component.component?.type ||
      component.type ||
      ""
    ).toLowerCase();
    const key =
      component.key || (component.component && component.component.key);
    const gv = key ? this.getGradingValidationForKey(key) : null;

    if (this.usesNumericLongitudinalRules(compType, gv)) return;

    this.checkPreviousVisitConsistency(changed);
  }

  checkPreviousVisitConsistency(changed) {
    if (this._validationSuppressDepth > 0) return;
    const component =
      changed?.changed?.component || changed?.flags?.changed?.component;
    if (!component) return;
    const key =
      component.key || (component.component && component.component.key);
    if (!key || typeof key !== "string") return;
    const gv = this.getGradingValidationForKey(key);
    if (!gv || gv.status !== "Required") return;

    const compareTo = (gv.compare_to || "previous").toLowerCase();
    let dataSource;
    if (compareTo === "custom" && gv.reference_visit_id) {
      dataSource =
        this.dataByMode &&
        this.dataByMode.byVisit &&
        this.dataByMode.byVisit[String(gv.reference_visit_id)];
      if (!dataSource || dataSource.completed === false) {
        this._showReferenceVisitNotAvailableAlert(
          dataSource && dataSource.previous_visit_label
            ? dataSource.previous_visit_label
            : "selected visit",
        );
        return;
      }
    } else {
      dataSource = this.dataByMode && this.dataByMode[compareTo];
      if (dataSource && dataSource.completed === false) {
        this._showReferenceVisitNotAvailableAlert(
          dataSource.previous_visit_label || "previous visit",
        );
        return;
      }
    }
    if (!dataSource || !dataSource.data) return;

    const type = (
      component.type ||
      component.component?.type ||
      ""
    ).toLowerCase();
    const optionTypes = [
      "radio",
      "selectboxes",
      "textfield",
      "textarea",
      "select",
    ];
    const isNumber = type === "number";
    const useNumericRules = this.usesNumericLongitudinalRules(type, gv);
    if (!optionTypes.includes(type) && !isNumber) return;

    let selectedReference = dataSource;
    let previousValue = this.getDataValueByQuestionKey(dataSource.data, key);
    if (
      compareTo === "previous" &&
      Array.isArray(dataSource.history) &&
      dataSource.history.length > 0
    ) {
      previousValue = undefined;
      for (let i = 0; i < dataSource.history.length; i++) {
        const candidate = dataSource.history[i] || {};
        const candidateValue = this.getDataValueByQuestionKey(
          candidate.data,
          key,
        );
        if (
          this.hasReferenceValue(candidateValue) &&
          !this.isLongitudinalSkipValue(candidateValue)
        ) {
          // History entries may omit user meta — keep parent reference identity.
          selectedReference = {
            ...dataSource,
            ...candidate,
            reference_user_name:
              candidate.reference_user_name || dataSource.reference_user_name,
            reference_user_role:
              candidate.reference_user_role || dataSource.reference_user_role,
            transfer_status:
              candidate.transfer_status || dataSource.transfer_status,
          };
          previousValue = candidateValue;
          break;
        }
      }
      if (previousValue === undefined) {
        this._showReferenceVisitNotAvailableAlert(
          selectedReference.previous_visit_label ||
            dataSource.previous_visit_label ||
            "previous visit",
        );
        return;
      }
    }
    if (
      (compareTo === "custom" || compareTo === "previous") &&
      (previousValue === undefined || previousValue === null)
    ) {
      this._showReferenceVisitNotAvailableAlert(
        dataSource.previous_visit_label ||
          (compareTo === "custom" ? "selected visit" : "previous visit"),
      );
      return;
    }
    let currentValue = this.gradingForm.data?.[key];
    if (component && typeof component.getValue === "function") {
      try {
        const fromComp = component.getValue();
        if (fromComp !== undefined) currentValue = fromComp;
      } catch (e) {}
    }
    const valueHint = changed?.flags?.valueHint;
    if (
      valueHint !== undefined &&
      valueHint !== null &&
      valueHint !== ""
    ) {
      currentValue = valueHint;
    }
    if (!this.hasReferenceValue(currentValue)) return;
    if (this.isLongitudinalSkipValue(currentValue)) return;
    if (this.isLongitudinalSkipValue(previousValue)) return;
    let numberFieldOverride = null;
    let transitionRule = null;

    // Avoid repeated popups for the same change once override is already captured.
    if (
      this.hasExistingOverrideForCurrentChange(key, previousValue, currentValue)
    ) {
      return;
    }
    if (this._shouldSkipDuplicateAlert(key, previousValue, currentValue)) {
      return;
    }

    if (useNumericRules && gv.base_on === "not_less_than_previous") {
      const prevNum = parseFloat(previousValue);
      const currNum = parseFloat(currentValue);
      if (isNaN(prevNum) || isNaN(currNum)) return;
      if (currNum >= prevNum) return;

      numberFieldOverride = {
        key: key,
        previous_value: prevNum,
        current_value: currNum,
        base_on: gv.base_on,
        operator: ">=",
      };
    } else if (
      useNumericRules &&
      (gv.base_on === "Custom" ||
        gv.base_on === "Percentage" ||
        gv.base_on === "absolute_difference")
    ) {
      const prevNum = parseFloat(previousValue);
      const currNum = parseFloat(currentValue);
      if (isNaN(prevNum) && isNaN(currNum)) return;
      if (isNaN(prevNum) || isNaN(currNum)) return;

      const rawThreshold = parseFloat(gv.custom_value);
      const threshold = isNaN(rawThreshold) ? 0 : rawThreshold;
      const op = (gv.operator || ">").toString();
      const compareWithOperator = (left, operator, right) => {
        if (operator === ">") return left > right;
        if (operator === ">=") return left >= right;
        if (operator === "<") return left < right;
        if (operator === "<=") return left <= right;
        if (operator === "=" || operator === "==") return left === right;
        return false;
      };

      let exceeded = false;
      if (gv.base_on === "Percentage") {
        if (isNaN(rawThreshold)) return;
        if (prevNum !== 0) {
          const absChangePct = Math.abs(((currNum - prevNum) / prevNum) * 100);
          exceeded = compareWithOperator(absChangePct, op, threshold);
        } else {
          // preserve existing fallback behavior when reference is zero
          const absChange = Math.abs(currNum - prevNum);
          exceeded = compareWithOperator(absChange, op, threshold);
        }
      } else if (gv.base_on === "absolute_difference") {
        if (isNaN(rawThreshold)) return;
        const absDiff = Math.abs(currNum - prevNum);
        exceeded = compareWithOperator(absDiff, op, threshold);
      } else {
        // Custom (directional, reference-based):
        // > / >= compares against (reference + threshold)
        // < / <= compares against (reference - threshold)
        // =  compares against (reference + threshold)
        let target = prevNum;
        if (op === ">" || op === ">=" || op === "=" || op === "==") {
          target = prevNum + threshold;
        } else if (op === "<" || op === "<=") {
          target = prevNum - threshold;
        }
        exceeded = compareWithOperator(currNum, op, target);
      }

      if (!exceeded) return;
      numberFieldOverride = {
        key: key,
        previous_value: prevNum,
        current_value: currNum,
        max_allowed: threshold,
        base_on: gv.base_on,
        operator: gv.operator || null,
      };
    } else if (gv.base_on === "Transition Rules") {
      transitionRule = this.findTransitionRule(gv, previousValue, currentValue);
      if (!transitionRule) return;
      if ((transitionRule.rule_type || "Allowed") === "Allowed") return;
    } else {
      return;
    }

    const prevDisplay = useNumericRules
      ? this.formatValueForDisplay(previousValue, previousValue)
      : this.formatValueForDisplay(
          this.getLabelFromSchema(this.gradingFormSchema, key, previousValue),
          previousValue,
        );
    const currDisplay = useNumericRules
      ? this.formatValueForDisplay(currentValue, currentValue)
      : this.formatValueForDisplay(
          this.getLabelFromSchema(this.gradingFormSchema, key, currentValue),
          currentValue,
        );
    const visitInfo =
      selectedReference.previous_visit_label || "Last Non-Missing";
    const dateStr = selectedReference.previous_date
      ? new Date(selectedReference.previous_date).toLocaleDateString()
      : "";
    const referenceVisitLine =
      "Reference Visit: " + visitInfo + (dateStr ? " (" + dateStr + ")" : "");
    const referenceUserLine = this.formatReferenceUserLine(
      selectedReference,
      true,
    );
    const referenceUserPlainLine = this.formatReferenceUserLine(
      selectedReference,
      false,
    );
    const prevSafe = String(prevDisplay)
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
    const currSafe = String(currDisplay)
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");

    const transitionEnforcement = transitionRule?.enforcement_level;
    const alertType = transitionEnforcement
      ? transitionEnforcement.toLowerCase()
      : (gv.alert_type || "Warning").toLowerCase();
    // Tier 1 = error (Hard Stop), Tier 2 = warning, Tier 3 = info
    const swalIcon =
      alertType === "hard stop" || alertType === "error"
        ? "error"
        : alertType === "info" || alertType === "success"
          ? "info"
          : "warning";

    const commonBlock =
      '<p class="text-left mb-2"><strong>Reference Visit:</strong> ' +
      visitInfo +
      (dateStr ? " (" + dateStr + ")" : "") +
      "</p>" +
      referenceUserLine +
      '<p class="text-left mb-2"><strong>Resolved Reference Value:</strong> "' +
      prevSafe +
      '"</p>' +
      '<p class="text-left mb-2"><strong>Current Value:</strong> "' +
      currSafe +
      '"</p>';

    let title;
    let message;
    let confirmText;
    let showCancelButton = true;
    const transitionType = transitionRule?.rule_type;
    const overrideAllowed = transitionRule
      ? transitionRule.override_allowed !== false
      : true;
    const isHardStop = swalIcon === "error";

    if (isHardStop) {
      title = "Longitudinal Consistency Hard Stop";
      message =
        commonBlock +
        '<p class="text-left mt-3 mb-2">A Tier 1 longitudinal discrepancy has been detected based on the configured transition rule for this question.</p>' +
        '<p class="text-left mb-2"><strong>Rule Type:</strong> ' +
        (transitionType || "Configured Rule") +
        "</p>" +
        '<p class="text-left mb-2">This violation must be explicitly resolved before Finalize.</p>' +
        '<p class="text-left mb-0">If you submit an override, a reason is required and will be shown to the adjudicator.</p>';
      confirmText = overrideAllowed ? "Submit Override" : "Resolved Edit";
    } else if (swalIcon === "info") {
      title = "Longitudinal Consistency Information";
      message =
        commonBlock +
        '<p class="text-left mt-3 mb-2">This transition matched a configured informational rule.</p>' +
        '<p class="text-left mb-0"><strong>Rule Type:</strong> ' +
        (transitionType || "Conditional") +
        "</p>";
      confirmText = "OK";
      showCancelButton = false;
    } else {
      title = "Longitudinal Consistency Warning";
      message =
        commonBlock +
        '<p class="text-left mt-3 mb-2">This response differs from the resolved reference value according to the configured longitudinal rule.</p>' +
        '<p class="text-left mb-2">Please review this discrepancy and either update the value, revert to the reference value, or provide an override reason.</p>';
      confirmText = overrideAllowed ? "Submit Override" : "Resolved Edit";
    }

    const self = this;
    const referenceUserMeta = {
      reference_user_name: selectedReference.reference_user_name || null,
      reference_user_role: selectedReference.reference_user_role || null,
      transfer_status: selectedReference.transfer_status || null,
    };

    const buildTransitionOverridePayload = (reasonText) => ({
      type: "transition_rule",
      previous_value: previousValue,
      current_value: currentValue,
      from_value: transitionRule?.from_value,
      to_value: transitionRule?.to_value,
      rule_type: transitionRule?.rule_type,
      enforcement_level: transitionRule?.enforcement_level,
      override_allowed: overrideAllowed,
      override_reason: reasonText || null,
      ...referenceUserMeta,
    });

    const buildNumberOverridePayload = (reasonText) => ({
      previous_value: numberFieldOverride.previous_value,
      current_value: numberFieldOverride.current_value,
      max_allowed: numberFieldOverride.max_allowed,
      base_on: numberFieldOverride.base_on,
      operator: numberFieldOverride.operator || null,
      override_reason: reasonText || null,
      ...referenceUserMeta,
    });

    if (typeof window.Swal === "undefined") {
      this._enqueueLongitudinalAlert(() => {
        self._longitudinalAlertVisible = true;
        self._lastAlertSnapshot[key] = {
          previousValue,
          currentValue,
        };

        const tierMsg =
          swalIcon === "error"
            ? "A Tier 1 longitudinal discrepancy has been detected based on the configured transition rule for this question.\n\nThis violation must be explicitly resolved before Finalize."
            : swalIcon === "info"
              ? "This transition matched a configured informational rule."
              : "This transition matched a configured warning rule.";
        const plainMsg =
          title +
          "\n\n" +
          referenceVisitLine +
          referenceUserPlainLine +
          '\nResolved Reference Value: "' +
          prevDisplay +
          '"\nCurrent Value: "' +
          currDisplay +
          '"\n\n' +
          tierMsg;
        const ok = window.confirm(plainMsg);
        if (ok) {
          if (!overrideAllowed) {
            self._handleResolvedEdit(key);
            self._finishLongitudinalAlert();
            return;
          }
          let overrideReason = null;
          if (isHardStop) {
            overrideReason = window.prompt(
              "Override reason is required for this hard stop. This reason will be shown to adjudicator:",
            );
            if (!overrideReason || !String(overrideReason).trim()) {
              window.alert("Override reason is required.");
              self._handleResolvedEdit(key);
              self._finishLongitudinalAlert();
              return;
            }
            overrideReason = String(overrideReason).trim();
          }
          self.overrides[key] =
            numberFieldOverride && numberFieldOverride.key === key
              ? buildNumberOverridePayload(overrideReason)
              : buildTransitionOverridePayload(overrideReason);
        } else {
          self._handleResolvedEdit(key);
        }
        self._finishLongitudinalAlert();
      });
      return;
    }
    const isSubmitOverride = confirmText === "Submit Override";
    const confirmBtnColor = isSubmitOverride ? "#d33" : "#3085d6";
    const cancelBtnColor = isSubmitOverride ? "#3085d6" : "#d33";

    const requiresInlineReason =
      isHardStop && overrideAllowed && isSubmitOverride;
    const showOptionalWarningComment =
      !isHardStop &&
      swalIcon === "warning" &&
      overrideAllowed &&
      isSubmitOverride;

    const fireAlert = () =>
      window.Swal.fire({
        icon: swalIcon,
        title: title,
        html: message,
        width: 640,
        heightAuto: false,
        returnFocus: false,
        allowOutsideClick: false,
        input:
          requiresInlineReason || showOptionalWarningComment
            ? "textarea"
            : undefined,
        inputLabel: requiresInlineReason
          ? "Override reason (required)"
          : showOptionalWarningComment
            ? "Override comment (optional)"
            : undefined,
        inputPlaceholder: requiresInlineReason
          ? "Provide clinical/context reason for adjudicator..."
          : showOptionalWarningComment
            ? "Optional comment for adjudicator..."
            : undefined,
        inputAttributes:
          requiresInlineReason || showOptionalWarningComment
            ? { "aria-label": "Override reason/comment" }
            : undefined,
        inputValidator: requiresInlineReason
          ? (value) =>
              !value || !String(value).trim()
                ? "Override reason is required."
                : undefined
          : undefined,
        showCancelButton: showCancelButton,
        confirmButtonColor: confirmBtnColor,
        cancelButtonColor: cancelBtnColor,
        confirmButtonText: confirmText,
        cancelButtonText: showCancelButton
          ? confirmText === "Resolved Edit"
            ? "Cancel"
            : "RESOLVED EDIT"
          : undefined,
        didOpen: (popup) => {
          popup.style.fontSize = "13px";
          const titleEl = popup.querySelector(".swal2-title");
          if (titleEl) {
            titleEl.style.fontSize = "1.1rem";
            titleEl.style.padding = "0.4rem 0.6rem 0.2rem";
          }
          const htmlContainer = popup.querySelector(".swal2-html-container");
          if (htmlContainer) {
            htmlContainer.style.maxHeight = "none";
            htmlContainer.style.overflow = "visible";
            htmlContainer.style.fontSize = "0.82rem";
            htmlContainer.style.lineHeight = "1.35";
            htmlContainer.style.margin = "0.5rem 0.8rem";
          }
          const actions = popup.querySelector(".swal2-actions");
          if (actions) {
            actions.style.marginTop = "0.7rem";
          }
          const inputEl = popup.querySelector(".swal2-textarea");
          if (inputEl) {
            inputEl.style.fontSize = "0.82rem";
          }
        },
        didClose: () => {
          self._finishLongitudinalAlert();
        },
      });

    this._enqueueLongitudinalAlert(() => {
      self._longitudinalAlertVisible = true;
      self._lastAlertSnapshot[key] = {
        previousValue,
        currentValue,
      };

      return fireAlert().then(function (result) {
        if (result.isConfirmed) {
          if (!overrideAllowed) {
            self._handleResolvedEdit(key);
            return;
          }
          const overrideReason = requiresInlineReason
            ? String(result.value || "").trim()
            : showOptionalWarningComment
              ? result.value
                ? String(result.value).trim()
                : null
              : null;
          self.overrides[key] =
            numberFieldOverride && numberFieldOverride.key === key
              ? buildNumberOverridePayload(overrideReason)
              : buildTransitionOverridePayload(overrideReason);
          return;
        }
        if (
          result.dismiss === window.Swal.DismissReason.cancel &&
          showCancelButton
        ) {
          self._handleResolvedEdit(key);
        }
      });
    });
  }

  revertValue(key, previousValue) {
    try {
      const comp = this.gradingForm.getComponent(key);
      if (comp && typeof comp.setValue === "function") {
        comp.setValue(previousValue);
      } else {
        this.gradingForm.data[key] = previousValue;
        this.gradingForm.redraw();
      }
    } catch (e) {
      console.warn("Could not revert previous visit value for", key, e);
    }
  }

  clearValue(key) {
    this._runWithoutValidation(() => {
      delete this.overrides[key];
      const silentFlags = { noUpdateEvent: true };

      try {
        const comp = this.gradingForm.getComponent(key);
        if (!comp) {
          if (this.gradingForm && this.gradingForm.data) {
            this.gradingForm.data[key] = null;
          }
          return;
        }

        const type = (
          (comp.component && comp.component.type) ||
          comp.type ||
          ""
        ).toLowerCase();
        let emptyValue = "";
        if (type === "number") {
          emptyValue = null;
        } else if (type === "checkbox") {
          emptyValue = false;
        } else if (type === "selectboxes") {
          emptyValue = {};
        } else if (type === "radio" || type === "select") {
          emptyValue = null;
        }

        if (typeof comp.setValue === "function") {
          comp.setValue(emptyValue, silentFlags);
        }

        if (this.gradingForm && this.gradingForm.data) {
          this.gradingForm.data[key] = emptyValue;
        }

        if (comp.element) {
          if (type === "radio") {
            comp.element
              .querySelectorAll('input[type="radio"]')
              .forEach((input) => {
                input.checked = false;
              });
          } else if (type === "checkbox" || type === "selectboxes") {
            comp.element
              .querySelectorAll('input[type="checkbox"]')
              .forEach((input) => {
                input.checked = false;
              });
          } else if (type === "select") {
            const select = comp.element.querySelector("select");
            if (select) select.value = "";
          } else {
            comp.element
              .querySelectorAll("input, textarea, select")
              .forEach((input) => {
                if (input.type === "checkbox" || input.type === "radio") {
                  input.checked = false;
                } else {
                  input.value = "";
                }
              });
          }
        }

        if (typeof comp.redraw === "function") {
          comp.redraw();
        } else if (typeof comp.refresh === "function") {
          comp.refresh();
        }
      } catch (e) {
        console.warn("Could not clear value for", key, e);
      }
    });
  }

  _focusField(key) {
    try {
      const comp = this.gradingForm && this.gradingForm.getComponent(key);
      const focusTarget =
        comp && comp.element
          ? comp.element.querySelector("input, textarea, select")
          : null;
      if (focusTarget && typeof focusTarget.focus === "function") {
        focusTarget.focus();
      }
    } catch (e) {}
  }

  valuesEqual(a, b) {
    if (a === b) return true;
    if (a == null && b == null) return true;
    if (typeof a === "object" && typeof b === "object") {
      try {
        return JSON.stringify(a) === JSON.stringify(b);
      } catch {
        return false;
      }
    }
    const sa = typeof a === "string" ? a.trim() : String(a);
    const sb = typeof b === "string" ? b.trim() : String(b);
    return sa === sb;
  }

  formatValueForDisplay(labelOrValue, rawValue) {
    const v =
      labelOrValue !== undefined && labelOrValue !== null
        ? labelOrValue
        : rawValue;
    if (Array.isArray(v)) return v.join(", ");
    return String(v != null ? v : "");
  }

  formatReferenceUserLine(reference, asHtml) {
    const name = reference && reference.reference_user_name;
    const role = reference && reference.reference_user_role;
    const transferStatus = reference && reference.transfer_status;
    if (!name && !role && !transferStatus) return asHtml ? "" : "";

    let label = "";
    if (role && name) {
      label = role + " (" + name + ")";
    } else if (name) {
      label = name;
    } else if (role) {
      label = role;
    }
    if (transferStatus) {
      label = label ? label + " · " + transferStatus : transferStatus;
    }

    if (asHtml) {
      const safe = PreviousVisitValidationModule.escapeHtml(label);
      return (
        '<p class="text-left mb-2"><strong>Reference by:</strong> ' +
        safe +
        "</p>"
      );
    }
    return "\nReference by: " + label;
  }

  /**
   * Resolve the loaded reference visit payload for a question key
   * (previous / custom visit), including name + grader/adjudicator role.
   */
  getReferenceDataSourceForKey(key) {
    const gv = this.getGradingValidationForKey(key);
    if (!gv) return null;
    const compareTo = (gv.compare_to || "previous").toLowerCase();
    if (compareTo === "custom" && gv.reference_visit_id) {
      return (
        (this.dataByMode &&
          this.dataByMode.byVisit &&
          this.dataByMode.byVisit[String(gv.reference_visit_id)]) ||
        null
      );
    }
    return (this.dataByMode && this.dataByMode.previous) || null;
  }

  /**
   * Fill missing reference_user_* on a stored override from the live
   * previous-visit payload (so adjudication flags work for older saves).
   */
  enrichOverrideWithReferenceUser(override, questionKey) {
    if (!override || typeof override !== "object") return override;
    if (override.reference_user_name && override.reference_user_role)
      return override;

    const dataSource = this.getReferenceDataSourceForKey(questionKey);
    if (!dataSource) return override;

    return {
      ...override,
      reference_user_name:
        override.reference_user_name || dataSource.reference_user_name || null,
      reference_user_role:
        override.reference_user_role || dataSource.reference_user_role || null,
      transfer_status:
        override.transfer_status || dataSource.transfer_status || null,
    };
  }

  getOverrides() {
    return this.overrides || {};
  }

  static escapeHtml(str) {
    if (str === undefined || str === null) return "";
    return String(str)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");
  }

  /**
   * Resolve Form.io component label by key (nested panels supported).
   */
  static findComponentLabel(schema, key) {
    if (!schema || !key) return key;
    const root = Array.isArray(schema) ? schema : schema.components;
    if (!Array.isArray(root)) return key;
    const stack = root.slice();
    while (stack.length) {
      const c = stack.pop();
      if (!c) continue;
      if (c.key === key) return c.label || key;
      if (Array.isArray(c.components) && c.components.length) {
        for (let i = 0; i < c.components.length; i++)
          stack.push(c.components[i]);
      }
    }
    return key;
  }

  static _longitudinalReportTone(enforcementLevel) {
    const e = String(enforcementLevel || "").toLowerCase();
    if (e.includes("hard")) return "hard";
    if (e.includes("warning")) return "warning";
    if (e.includes("info")) return "info";
    return "default";
  }

  /**
   * Rich HTML for the longitudinal changes report (SweetAlert html).
   * @param {object} schema - grading form schema
   * @param {object} overrides - previous_visit_overrides map
   * @param {function} getLabelFromSchema - (schema, questionKey, value) => label
   */
  static buildLongitudinalReportHtml(schema, overrides, getLabelFromSchema) {
    const keys = Object.keys(overrides || {});
    if (!keys.length) return "";
    const esc = PreviousVisitValidationModule.escapeHtml;
    const labelFor = (questionKey, val) => {
      try {
        const L = getLabelFromSchema(schema, questionKey, val);
        if (Array.isArray(L))
          return L.map((x) => esc(String(x != null ? x : ""))).join(", ");
        return esc(String(L != null ? L : ""));
      } catch {
        return esc(String(val != null ? val : ""));
      }
    };

    const items = keys.map((questionKey) => ({
      questionKey,
      o: overrides[questionKey] || {},
      qLabel: PreviousVisitValidationModule.findComponentLabel(
        schema,
        questionKey,
      ),
    }));
    items.sort((a, b) =>
      String(a.qLabel).localeCompare(String(b.qLabel), undefined, {
        sensitivity: "base",
      }),
    );

    const tones = {
      hard: { border: "#dc2626", change: "#b91c1c" },
      /* Golden / yellow warning — clearly separate from hard-stop red */
      warning: { border: "#eab308", change: "#ca8a04" },
      info: { border: "#9ca3af", change: "#1f2937" },
      default: { border: "#d1d5db", change: "#1f2937" },
    };

    const wrap = [
      '<div style="text-align:left;font-size:13px;line-height:1.45;max-height:70vh;overflow-y:auto;padding:2px 4px 8px 0;">',
    ];

    for (let i = 0; i < items.length; i++) {
      const { questionKey, o, qLabel } = items[i];
      const isTransition = o.type === "transition_rule";
      const isNumber =
        o.base_on === "Percentage" ||
        o.base_on === "Custom" ||
        o.base_on === "absolute_difference";
      const isNotLessThanPrevious = o.base_on === "not_less_than_previous";
      const fromVal = isTransition ? o.from_value : o.previous_value;
      const toVal = isTransition ? o.to_value : o.current_value;
      const fromText = labelFor(questionKey, fromVal);
      const toText = labelFor(questionKey, toVal);

      let toneKey = "default";
      if (isTransition && o.enforcement_level) {
        toneKey = PreviousVisitValidationModule._longitudinalReportTone(
          o.enforcement_level,
        );
      }
      const t = tones[toneKey] || tones.default;

      let metaBlock = "";
      if (isNumber) {
        const th =
          o.max_allowed != null
            ? esc(String(o.max_allowed)) +
              (o.base_on === "Percentage" ? "%" : "")
            : "—";
        const opText =
          o.base_on === "absolute_difference" && o.operator
            ? " (" + esc(String(o.operator)) + ")"
            : "";
        metaBlock =
          '<div style="margin-top:8px;font-size:11px;color:#6b7280;">Numeric longitudinal rule · <strong style="color:#374151;">' +
          esc(String(o.base_on || "")) +
          opText +
          '</strong> · Threshold: <strong style="color:#374151;">' +
          th +
          "</strong></div>";
      } else if (isNotLessThanPrevious) {
        metaBlock =
          '<div style="margin-top:8px;font-size:11px;color:#6b7280;">Numeric longitudinal rule · <strong style="color:#374151;">Not Less Than Previous</strong> · Current must be ≥ reference</div>';
      }

      const reason = o.override_reason;
      const reasonInline = reason
        ? '<span style="font-size:12px;color:#4b5563;line-height:1.45;flex:1 1 220px;min-width:min(100%,200px);">' +
          '<span style="color:#6b7280;font-weight:600;">Comment:</span> ' +
          '<span style="white-space:pre-wrap;word-break:break-word;">' +
          esc(String(reason)) +
          "</span></span>"
        : "";

      const refUser =
        PreviousVisitValidationModule.formatReferenceUserPrefix(o);
      const refUserBlock = refUser
        ? '<div style="margin-top:6px;font-size:11px;color:#6b7280;">Reference by <strong style="color:#374151;">' +
          esc(refUser) +
          "</strong></div>"
        : "";

      wrap.push(
        '<div style="border:1px solid #e5e7eb;border-radius:10px;padding:14px 16px;margin-bottom:14px;border-left:4px solid ' +
          t.border +
          ';background:#fafafa;box-shadow:0 1px 2px rgba(0,0,0,0.04);">' +
          '<div style="display:flex;flex-wrap:wrap;align-items:flex-start;gap:8px 16px;column-gap:20px;margin-bottom:10px;">' +
          '<span style="font-weight:700;font-size:14px;color:#111827;line-height:1.35;flex:1 1 200px;">' +
          esc(String(qLabel)) +
          "</span>" +
          reasonInline +
          "</div>" +
          '<div style="font-size:13px;font-weight:600;color:' +
          t.change +
          ';">' +
          fromText +
          ' <span style="color:#9ca3af;font-weight:500;padding:0 4px;">→</span> ' +
          toText +
          "</div>" +
          refUserBlock +
          metaBlock +
          "</div>",
      );
    }

    wrap.push("</div>");
    return wrap.join("");
  }

  /**
   * Opens SweetAlert with formatted longitudinal report (or empty state).
   */
  static openLongitudinalChangesReportModal(
    schema,
    overrides,
    getLabelFromSchema,
  ) {
    const html = PreviousVisitValidationModule.buildLongitudinalReportHtml(
      schema,
      overrides,
      getLabelFromSchema,
    );
    if (typeof window.Swal === "undefined") {
      window.alert(
        html
          ? "Longitudinal changes recorded — enable SweetAlert2 for full report."
          : "No longitudinal changes recorded.",
      );
      return Promise.resolve();
    }
    if (!html) {
      return window.Swal.fire({
        icon: "info",
        title: "No Longitudinal Changes Recorded",
        html: "There are no stored longitudinal overrides for this submission.",
        width: 720,
      });
    }
    return window.Swal.fire({
      icon: "info",
      title: "Longitudinal Changes Report",
      html,
      width: 960,
      confirmButtonText: "OK",
      heightAuto: false,
      didOpen: (popup) => {
        popup.style.fontSize = "13px";
        const titleEl = popup.querySelector(".swal2-title");
        if (titleEl) titleEl.style.marginBottom = "0.35rem";
        const htmlContainer = popup.querySelector(".swal2-html-container");
        if (htmlContainer) {
          htmlContainer.style.maxHeight = "none";
          htmlContainer.style.overflow = "visible";
          htmlContainer.style.margin = "0.4rem 0.6rem 0";
          htmlContainer.style.padding = "0";
        }
      },
    });
  }

  /**
   * On final submit (status completed): show the longitudinal report first; submit only if user confirms.
   * @returns {Promise<boolean>} true = proceed with submit, false = user cancelled
   */
  static confirmLongitudinalReportBeforeSubmit(
    schema,
    overrides,
    getLabelFromSchema,
    status,
  ) {
    if (status !== "completed") return Promise.resolve(true);
    const keys = Object.keys(overrides || {});
    if (!keys.length) return Promise.resolve(true);
    const html = PreviousVisitValidationModule.buildLongitudinalReportHtml(
      schema,
      overrides,
      getLabelFromSchema,
    );
    if (!html) return Promise.resolve(true);
    if (typeof window.Swal === "undefined") {
      const ok = window.confirm(
        "Review longitudinal changes before submitting. Click OK to submit the form.",
      );
      return Promise.resolve(ok);
    }
    return window.Swal.fire({
      icon: "info",
      title: "Review Longitudinal Changes Before Submit",
      html,
      width: 960,
      showCancelButton: true,
      confirmButtonText: "OK, Submit Form",
      cancelButtonText: "Back",
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#6b7280",
      heightAuto: false,
      didOpen: (popup) => {
        popup.style.fontSize = "13px";
        const titleEl = popup.querySelector(".swal2-title");
        if (titleEl) titleEl.style.marginBottom = "0.35rem";
        const htmlContainer = popup.querySelector(".swal2-html-container");
        if (htmlContainer) {
          htmlContainer.style.maxHeight = "none";
          htmlContainer.style.overflow = "visible";
          htmlContainer.style.margin = "0.4rem 0.6rem 0";
          htmlContainer.style.padding = "0";
        }
      },
    }).then((result) => !!result.isConfirmed);
  }

  static _formatDisplayValueForNote(value) {
    if (Array.isArray(value)) return value.join(", ");
    return String(value != null ? value : "");
  }

  /**
   * Build "Role (Name)" prefix for override notes / reports.
   * e.g. "Grader (Jane Doe)" or "Adjudicator (John Smith)"
   */
  static formatReferenceUserPrefix(override) {
    if (!override) return "";
    const name = override.reference_user_name;
    const role = override.reference_user_role;
    if (role && name) return role + " (" + name + ")";
    if (role) return String(role);
    if (name) return String(name);
    return "";
  }

  /**
   * For adjudication view: build hover note for overrides.
   * Supports value label mapping (e.g. 0/1 -> Absent/Present) when options are provided.
   * @param {object} override
   * @param {object} options - { schema, questionKey, getLabelFromSchema }
   * @returns {{ title: string, icon: string, className: string }|null}
   */
  static formatOverrideNote(override, options = {}) {
    if (!override) return null;
    const getMapped = (raw) => {
      try {
        if (
          options &&
          options.getLabelFromSchema &&
          options.schema &&
          options.questionKey
        ) {
          const mapped = options.getLabelFromSchema(
            options.schema,
            options.questionKey,
            raw,
          );
          return PreviousVisitValidationModule._formatDisplayValueForNote(
            mapped !== undefined && mapped !== null ? mapped : raw,
          );
        }
      } catch (_) {}
      return PreviousVisitValidationModule._formatDisplayValueForNote(raw);
    };

    const userPrefix =
      PreviousVisitValidationModule.formatReferenceUserPrefix(override);
    const userPart = userPrefix ? userPrefix + " · " : "";

    const isNumberExceeded =
      override.base_on === "Percentage" ||
      override.base_on === "Custom" ||
      override.base_on === "absolute_difference";
    const isNotLessThanPrevious = override.base_on === "not_less_than_previous";
    const isTransitionRule = override.type === "transition_rule";
    const title = isNotLessThanPrevious
      ? userPart +
        "Reference: " +
        getMapped(override.previous_value) +
        " -> Current: " +
        getMapped(override.current_value) +
        " (current must be ≥ reference)" +
        (override.override_reason
          ? " | Reason: " + override.override_reason
          : "")
      : isNumberExceeded
        ? userPart +
          "Reference: " +
          getMapped(override.previous_value) +
          " -> Current: " +
          getMapped(override.current_value) +
          ", threshold: " +
          (override.base_on === "absolute_difference" && override.operator
            ? override.operator + " "
            : "") +
          (override.base_on === "Percentage"
            ? override.max_allowed + "%"
            : override.max_allowed) +
          (override.override_reason
            ? " | Reason: " + override.override_reason
            : "")
        : isTransitionRule
          ? userPart +
            "Reference: " +
            getMapped(override.from_value) +
            " -> Current: " +
            getMapped(override.to_value) +
            (override.enforcement_level
              ? " (" + override.enforcement_level + ")"
              : "") +
            (override.override_reason
              ? " | Reason: " + override.override_reason
              : "")
          : userPart +
            "Reference: " +
            getMapped(override.previous_value) +
            ", grader selected: " +
            getMapped(override.current_value) +
            (override.override_reason
              ? " | Reason: " + override.override_reason
              : "");
    return {
      title,
      icon: "⚑",
      className: "text-red-600",
    };
  }
}
